import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle2, ClipboardCheck, Eye, Loader2, MessageSquareWarning } from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const DecisionDialog = ({ item, mode, onClose, onDone }) => {
  const { api } = useAuth();
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => setComment(''), [item, mode]);
  if (!item) return null;
  const approve = mode === 'approve';
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/course-review/courses/${item.id}/${mode}`, { comment });
      toast.success(approve ? 'Approved — the course is now live' : 'Changes requested — the author has been emailed');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="review-decision-dialog">
        <DialogHeader>
          <DialogTitle>{approve ? 'Approve and publish' : 'Request changes'}</DialogTitle>
          <DialogDescription>{item.title}</DialogDescription>
        </DialogHeader>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
          placeholder={approve ? 'Optional note for the author' : 'What needs changing? (required)'} data-testid="review-comment-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || (!approve && !comment.trim())}
            className={approve ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'} data-testid="review-decision-confirm">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{approve ? 'Approve & publish' : 'Send back'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const QueueItem = ({ item, onDecide, onOpen }) => (
  <Card className="border-slate-200" data-testid={`review-item-${item.id}`}>
    <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-[#2a3e5d]">{item.title}</p>
          <Badge className={item.is_update ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}>
            {item.is_update ? 'Update to live course' : 'New course'}
          </Badge>
        </div>
        <p className="text-xs text-slate-500">
          Submitted by {item.submitted_by ? `${item.submitted_by.first_name} ${item.submitted_by.last_name}` : 'unknown'} · {fmt(item.submitted_at)}
        </p>
        <p className="text-xs text-slate-500" data-testid={`review-counts-${item.id}`}>
          Draft: {item.draft.modules} modules, {item.draft.elements} elements
          {item.live && <> · Live now: {item.live.modules} modules, {item.live.elements} elements</>}
        </p>
        {item.note && <p className="text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2 mt-2">“{item.note}”</p>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => onOpen(item)} data-testid={`review-open-${item.id}`}><Eye className="h-4 w-4 mr-1" /> Review content</Button>
        <Button variant="outline" size="sm" className="text-amber-700 border-amber-300" onClick={() => onDecide(item, 'reject')} data-testid={`review-reject-${item.id}`}>
          <MessageSquareWarning className="h-4 w-4 mr-1" /> Request changes
        </Button>
        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onDecide(item, 'approve')} data-testid={`review-approve-${item.id}`}>
          <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
        </Button>
      </div>
    </CardContent>
  </Card>
);

export default function AdminApprovalsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/course-review/queue').then((r) => setItems(r.data.items || [])).catch(() => toast.error('Could not load approvals')).finally(() => setLoading(false));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="admin-approvals-page">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Course Approvals</h1>
          <p className="text-sm text-slate-500 mt-1">Courses and changes submitted by Content Managers. Nothing goes live until you approve it.</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
        ) : items.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-16 text-center" data-testid="approvals-empty">
            <ClipboardCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">All caught up</p>
            <p className="text-sm text-slate-400">No courses are waiting for approval.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <QueueItem key={it.id} item={it} onDecide={(item, mode) => setDecision({ item, mode })} onOpen={(item) => navigate(`/admin/courses/${item.id}/edit`)} />
            ))}
          </div>
        )}
      </div>
      {decision && <DecisionDialog item={decision.item} mode={decision.mode} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); }} />}
    </DashboardLayout>
  );
}
